import React from "react";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeftRight, Sparkles } from "lucide-react";
import { AnaliseFrenteFundo, DezenaFrenteFundo } from "./frenteFundoEngine";
import { rotuloFonte } from "./palpitesUtils";

const formatarData = (data: string | null) => data
  ? new Date(`${data}T12:00:00`).toLocaleDateString("pt-BR")
  : "Sem registro";

const Ranking: React.FC<{ titulo: string; itens: DezenaFrenteFundo[] }> = ({ titulo, itens }) => (
  <div className="rounded-md border border-border bg-muted/30 p-2.5">
    <p className="mb-2 text-xs font-bold text-foreground">{titulo}</p>
    <div className="space-y-2">
      {itens.map((item) => (
        <div key={item.valor} className="rounded-md border border-border bg-background p-2 text-[11px]">
          <div className="flex items-center justify-between gap-2">
            <Badge variant="secondary" className="font-mono text-sm">{item.valor}</Badge>
            <strong>{item.ocorrencias}x · {item.percentual.toFixed(1)}%</strong>
          </div>
          <p className="mt-1 text-muted-foreground">Última: {formatarData(item.ultimaData)} · atraso: {item.atraso} corrida(s)</p>
          <p className="text-muted-foreground">Prêmios: {item.posicoes.length ? item.posicoes.map((p) => `${p}º`).join(", ") : "sem ocorrência do 1º ao 5º"}</p>
          <p className="text-muted-foreground">Turnos: {item.turnos.length ? item.turnos.join(", ") : "—"}</p>
        </div>
      ))}
    </div>
  </div>
);

export const DezenasFrenteFundoCard: React.FC<{ analise: AnaliseFrenteFundo; historico?: boolean }> = ({ analise, historico = false }) => {
  if (!analise.frentes.length || !analise.fundos.length) return null;
  return (
    <Card className="border-yellow-500/30 bg-background/90">
      <CardHeader className="p-3 pb-2">
        <CardTitle className="flex flex-wrap items-center gap-2 text-sm text-yellow-500">
          <ArrowLeftRight className="h-4 w-4" /> Dezenas Frente e Fundo
          <Badge variant="outline" className="text-[10px]">{rotuloFonte(analise.fonte)}</Badge>
        </CardTitle>
        <p className="text-[10px] text-muted-foreground">
          {historico ? "Fotografia preservada no momento da geração." : `${analise.totalSorteios} corridas e ${analise.totalResultados} resultados analisados.`}
        </p>
      </CardHeader>
      <CardContent className="space-y-3 p-3 pt-0">
        <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
          <Ranking titulo="4 dezenas mais sorteadas na frente" itens={analise.frentes} />
          <Ranking titulo="4 dezenas mais sorteadas no fundo" itens={analise.fundos} />
        </div>
        <div className="rounded-md border border-yellow-500/30 bg-muted/30 p-2.5">
          <p className="mb-2 flex items-center gap-1.5 text-xs font-bold text-yellow-500"><Sparkles className="h-3.5 w-3.5" /> 3 milhares mais fortes</p>
          <div className="space-y-2">
            {analise.milhares.map((item) => (
              <div key={item.milhar} className="rounded-md border border-border bg-background p-2.5">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge className="font-mono text-sm">{item.milhar}</Badge>
                  <span className="text-[11px] font-semibold">Frente {item.frente} + Fundo {item.fundo}</span>
                  <Badge variant="outline" className="ml-auto text-[10px]">Força {item.pontuacao}/100</Badge>
                </div>
                <p className="mt-1.5 text-[11px] leading-relaxed text-muted-foreground">{item.justificativa}</p>
              </div>
            ))}
          </div>
        </div>
        <p className="text-[10px] text-muted-foreground">Análise estatística baseada em ocorrências do 1º ao 5º prêmio. Resultados anteriores não garantem premiação futura.</p>
      </CardContent>
    </Card>
  );
};

export default DezenasFrenteFundoCard;